import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, storage } from '../firebase';
import { collection, addDoc, Timestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

export default function AddCarPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    make: '',
    model: '',
    rego: '',
    color: '',
    year: '',
  });
  const [notes, setNotes] = useState('');
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e, index) => {
    const file = e.target.files[0];
    if (!file) return;
    const newImages = [...images];
    newImages[index] = file;
    setImages(newImages);
  };

  const addImageField = () => {
    if (images.length < 8) setImages([...images, null]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (images.length === 0 || !images[0]) {
      alert('Please upload at least one image.');
      return;
    }

    setLoading(true);
    try {
      // Upload images
      const imageUploadPromises = images
        .filter((img) => img)
        .map((img) => {
          const imageRef = ref(storage, `carImages/${Date.now()}-${img.name}`);
          return uploadBytes(imageRef, img).then(() => getDownloadURL(imageRef));
        });

      const imageUrls = await Promise.all(imageUploadPromises);

      // Create main car doc
      const carData = {
        ...formData,
        imageUrls,
        imageURL: imageUrls[0],
        status: 'available',
        createdAt: Timestamp.now(),
      };

      const carRef = await addDoc(collection(db, 'cars'), carData);

      // Add notes to subcollection if provided
      if (notes.trim()) {
        await addDoc(collection(db, `cars/${carRef.id}/notes`), {
          text: notes.trim(),
          imageUrl: '',
          createdAt: Timestamp.now(),
        });
      }

      navigate('/', { state: { successMsg: 'Car added!' } });
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={pageWrapper}>
      <div style={formCard}>
        <div style={formHeader}>
          <h2 style={formTitle}>🚘 Add New Car</h2>
          <button onClick={() => navigate('/')} style={backButton}>← Back</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={fieldGroup}>
            <label style={label}>Make</label>
            <input name="make" onChange={handleChange} required style={input} />
          </div>
          <div style={fieldGroup}>
            <label style={label}>Model</label>
            <input name="model" onChange={handleChange} required style={input} />
          </div>
          <div style={fieldGroup}>
            <label style={label}>Registration</label>
            <input name="rego" onChange={handleChange} required style={input} />
          </div>
          <div style={fieldGroup}>
            <label style={label}>Color</label>
            <input name="color" onChange={handleChange} required style={input} />
          </div>

          <div style={fieldGroup}>
            <label style={label}>Year</label>
            <input
              name="year"
              onChange={handleChange}
              required
              style={input}
              type="number"
              min="1900"
              max={new Date().getFullYear()}
              placeholder="e.g. 2020"
            />
          </div>

          <div style={fieldGroup}>
            <label style={label}>Notes (optional)</label>
            <textarea
              name="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              style={{ ...input, resize: 'vertical' }}
              placeholder="Any specific notes about this car..."
            />
          </div>

          <div style={fieldGroup}>
            <label style={label}>Car Images (max 8)</label>
            {images.map((img, index) => (
              <div key={index} style={{ marginBottom: '10px' }}>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageChange(e, index)}
                  style={{ ...input, padding: '10px' }}
                />
                {img && (
                  <img
                    src={URL.createObjectURL(img)}
                    alt="preview"
                    width="100"
                    style={{ marginTop: '5px', borderRadius: '8px' }}
                  />
                )}
              </div>
            ))}
            {images.length < 8 && (
              <button type="button" onClick={addImageField} style={addButton}>Add Image</button>
            )}
          </div>

          <button type="submit" disabled={loading} style={submitButton}>
            {loading ? (
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                <span className="spinner" style={spinnerStyle}></span> Saving...
              </span>
            ) : (
              'Add Car'
            )}
          </button>
        </form>
      </div>
    </div>
  );
}

// Styles
const pageWrapper = {
  backgroundColor: '#f4f6f8',
  minHeight: '100vh',
  display: 'flex',
  justifyContent: 'center',
  padding: '40px 20px',
};

const formCard = {
  backgroundColor: '#fff',
  borderRadius: '16px',
  padding: '32px',
  width: '100%',
  maxWidth: '500px',
  boxShadow: '0 8px 30px rgba(0,0,0,0.06)',
};

const formHeader = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '24px',
};

const formTitle = {
  fontSize: '22px',
  fontWeight: 600,
  color: '#111827',
};

const backButton = {
  padding: '6px 12px',
  fontSize: '14px',
  border: 'none',
  borderRadius: '8px',
  backgroundColor: '#e5e7eb',
  color: '#374151',
  fontWeight: 500,
  cursor: 'pointer',
};

const fieldGroup = { marginBottom: '18px' };

const label = {
  display: 'block',
  marginBottom: '6px',
  fontSize: '14px',
  fontWeight: 500,
  color: '#374151',
};

const input = {
  width: '100%',
  padding: '12px 14px',
  border: '1px solid #d1d5db',
  borderRadius: '10px',
  fontSize: '15px',
  backgroundColor: '#f9fafb',
  outline: 'none',
  boxSizing: 'border-box',
};

const submitButton = {
  width: '100%',
  padding: '14px',
  background: 'linear-gradient(to right, #2563eb, #3b82f6)',
  color: '#fff',
  border: 'none',
  borderRadius: '12px',
  fontSize: '16px',
  fontWeight: 600,
  cursor: 'pointer',
};

const addButton = {
  marginTop: '10px',
  padding: '10px 14px',
  borderRadius: '8px',
  border: '1px solid #ccc',
  backgroundColor: '#fff',
  cursor: 'pointer',
  fontWeight: 500,
};

const spinnerStyle = {
  width: '16px',
  height: '16px',
  border: '3px solid #fff',
  borderTop: '3px solid rgba(255, 255, 255, 0.3)',
  borderRadius: '50%',
  animation: 'spin 1s linear infinite',
};
